import { useMemo } from 'react';
import cn from 'classnames';
import { useAuthContext } from '@lp/core/auth';
import { ReservationCell } from '@lp/features/Scheduler/components';
import { SchedulerDayColumn } from '@lp/features/Scheduler/types';
import { getSShiftTypeColors } from '@lp/features/Scheduler/SchedulerSurgeons/utils';
import { SOrganGroup } from '@lp/features/Scheduler/SchedulerSurgeons/types';
import { shiftTypeNames } from '@/constants/schedule.constants';
import { ReservationNode } from '@/api/apis/resources';
import { SShift } from '@/api/apis/resources/schedule';
import { SchedulerPermissions } from '@/permissions';
import { CaseResourceType, OrganType } from '@/api/apis/case';

type SReservationCellProps = {
  organGroup: SOrganGroup;
  column: SchedulerDayColumn;
  shiftData?: SShift;
  reservations?: ReservationNode[];
};

export const SReservationCell = ({ column, shiftData, reservations, organGroup }: SReservationCellProps) => {
  const { employeeData, checkPermission } = useAuthContext();
  const colors = getSShiftTypeColors(shiftData);

  const hasEditPermission = useMemo(() => {
    const hasWritePermission = checkPermission(SchedulerPermissions.SSchedulesRestPeriodWrite);
    const hasSLiWritePermissions = checkPermission(SchedulerPermissions.SLiSchedulesRestPeriodWrite);
    const hasSHtLuWritePermission = checkPermission(SchedulerPermissions.SLiSchedulesRestPeriodWrite);

    const canEditSLi =
      hasSLiWritePermissions &&
      organGroup === SOrganGroup.Liver &&
      employeeData?.employee?.rsScheduleManagerOrgans.includes(OrganType.Liver);
    const canEditSHtLu =
      hasSHtLuWritePermission &&
      organGroup === SOrganGroup.HeartAndLungs &&
      employeeData?.employee?.rsScheduleManagerOrgans.some(
        (organ) => organ === OrganType.Heart || organ === OrganType.Lungs,
      );

    return hasWritePermission || canEditSLi || canEditSHtLu;
  }, [checkPermission, employeeData?.employee.rsScheduleManagerOrgans, organGroup]);

  return (
    <ReservationCell
      column={column}
      reservations={reservations}
      isRestReadOnly={!hasEditPermission}
      resourceType={CaseResourceType.Surgeon}
      organGroup={organGroup}
    >
      {shiftData && <span className={cn(colors.icon, 'text-xs-r')}>{shiftTypeNames[shiftData.shiftType]}</span>}
    </ReservationCell>
  );
};
